import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import type {
    BillToMinimalVm,
    CarrierMinimalVm,
    ModeVm,
    StatusCodeVm,
    AscReferenceCodeVm,
    InvoiceResearchOptionsVm,
} from '../rtkquery/paylogikApi';
import {paylogikApi} from '../rtkquery/paylogikApi';
import type {RootState} from './store';
import {store} from './store';
import {GridSize} from '@mui/material';
import {createGuid} from '../../functions/utility';
import {logout} from './authSlice';
import {Paging} from '../commonObjects';
import { ColumnState } from 'ag-grid-enterprise';

type BillToWidth = {md: GridSize; lg: GridSize; xl: GridSize};

interface SearchFormState extends InvoiceResearchOptionsVm {
    hasValue: boolean;
    billToWidth: BillToWidth;
}

type InvoiceResearchState = {
    currentSearch: SearchFormState;
    currentSearchKey: string;
    resetDataTablePagination: boolean;
    showResults: boolean;
    availableBillTos: BillToMinimalVm[];
    invoiceTabs: InvoiceTab[];
    currentTab: string;
    dataGridPaging: Paging;
    editInvoices?: boolean;
    sessionColumnState?: ColumnState;
};

type MultiSelectDispatchOptions<T> = {
    reason: 'createOption' | 'selectOption' | 'removeOption' | 'clear' | 'blur';
    details?: {option: T};
    value: T[];
};

type AddressDispatchOptions = {
    addressType: string;
    property: string;
    value: string;
};

type DateRangeDispatchOptions = {
    dateType: string;
    property: 'start' | 'end';
    value: any;
};

type EntryMethodDispatchOptions = {
    method: string;
    checked: boolean;
};

type InvoiceTab = {
    key: string;
    type: string;
    idx: number;
    display: string;
};

const changeBtWidth = (items) => {
    if (items > 3) {
        return {md: 12, lg: 12, xl: 12} as BillToWidth;
    } else {
        return {md: 6, lg: 4, xl: 3} as BillToWidth;
    }
};

const initialSearchState = {
    hasValue: false,
    billToWidth: {md: 6, lg: 4, xl: 3},
    entered: {},
    checkDate: {},
    processed: {},
    shipped: {},
    billTos: [],
    entryMethods: [],
    invoiceTypes: [],
    page: 1,
    recordCountRequested: 20,
    sortBy: undefined,
    sortAscending: undefined,
    declaredCount: undefined,
} as SearchFormState;

const baseSessionState = {
    currentSearch: initialSearchState,
    showResults: false,
    availableBillTos: [],
    invoiceTabs: [],
    currentTab: 'search',
    dataGridPaging: {},
    sessionColumnState: {},
} as InvoiceResearchState;

const slice = createSlice({
    name: 'invoiceResearch',
    initialState: baseSessionState,
    reducers: {
        addInvoiceTab: (state, action: PayloadAction<InvoiceTab>) => {
            state.invoiceTabs.push(action.payload);
        },
        removeInvoiceTab: (state, action: PayloadAction<string>) => {
            state.invoiceTabs = state.invoiceTabs.filter(
                (x) => x.key !== action.payload
            );
        },
        setCurrentTab: (state, action: PayloadAction<string>) => {
            state.currentTab = action.payload;
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentSearch.page = action.payload;
            state.currentSearchKey = createGuid();
        },
        setItemsPerPage: (state, action: PayloadAction<number>) => {
            state.currentSearch.recordCountRequested = action.payload;
            state.currentSearchKey = createGuid();
        },
        setDeclaredCount: (state, action: PayloadAction<number>) => {
            state.currentSearch.declaredCount = action.payload;
            state.currentSearchKey = createGuid();
        },
        setGridSort: (
            state,
            action: PayloadAction<{column: string; sortDirection: string}>
        ) => {
            // data.page = page;
            // data.recordCountRequested = recordsPerPage;
            // data.sortBy = sortCol?.selector;
            // data.sortAscending = !sortDir || sortDir?.toLowerCase() === 'asc';
            // data.declaredCount = declaredCount;
            state.currentSearch.sortBy = action.payload.column;
            state.currentSearch.sortAscending =
                !action.payload.sortDirection ||
                action.payload.sortDirection?.toLowerCase() === 'asc';
            state.currentSearchKey = createGuid();
        },
        resetPaging: (state) => {
            state.currentSearch.page = 1;
            state.currentSearch.recordCountRequested = 25;
            state.currentSearch.sortBy = undefined;
            state.currentSearch.sortAscending = undefined;
            state.currentSearch.declaredCount = undefined;
            state.currentSearchKey = createGuid();
        },
        resetInvoiceSearch: (state) => {
            state.currentSearch = initialSearchState;
            state.showResults = false;
            state.currentSearchKey = createGuid();
        },
        setInvoiceSearchValues: (
            state,
            action: PayloadAction<SearchFormState>
        ) => {
            state.currentSearch = action.payload;
            state.showResults = !!action.payload;
            state.currentSearchKey = createGuid();
            state.resetDataTablePagination = !state.resetDataTablePagination;
            state.currentSearch.page = 1;
        },
        setSearchCarrier: (
            state,
            action: PayloadAction<MultiSelectDispatchOptions<CarrierMinimalVm>>
        ) => {
            if (action.payload.reason === 'removeOption') {
                if (action.payload.value) {
                    state.currentSearch.hasValue = true;
                    state.currentSearch.carriers = action.payload.value;
                } else {
                    state.currentSearch.hasValue = true;
                    state.currentSearch.carriers = [];
                }
            } else if (action.payload.reason === 'selectOption') {
                if (action.payload.details.option.carrId === '*') {
                    state.currentSearch.hasValue = true;
                    state.currentSearch.carriers = [];
                } else {
                    state.currentSearch.hasValue = true;
                    state.currentSearch.carriers = action.payload.value;
                }
            } else {
                state.currentSearch.hasValue = true;
                state.currentSearch.carriers = action.payload.value;
            }
        },
        setSearchBillTo: (
            state,
            action: PayloadAction<MultiSelectDispatchOptions<BillToMinimalVm>>
        ) => {
            if (action.payload.reason === 'removeOption') {
                if (action.payload.value) {
                    const w = changeBtWidth(action.payload.value);
                    state.currentSearch.hasValue = true;
                    state.currentSearch.billTos = action.payload.value;
                    state.currentSearch.billToWidth = w;
                } else {
                    const w = changeBtWidth(1);
                    state.currentSearch.hasValue = true;
                    state.currentSearch.billTos = [];
                    state.currentSearch.billToWidth = w;
                }
            }
            if (action.payload.reason === 'selectOption') {
                if (action.payload.details.option.isParent) {
                    const children = state.availableBillTos.filter(
                        (x) =>
                            x.customerId ===
                                action.payload.details.option.btId &&
                            action.payload.details.option.btId !== x.btId &&
                            action.payload.value.indexOf(x) < 0
                    );

                    const bob = [...action.payload.value, ...children];

                    if (action.payload?.value) {
                        const w = changeBtWidth(bob.length);
                        state.currentSearch.hasValue = true;
                        state.currentSearch.billTos = [...bob];
                        state.currentSearch.billToWidth = w;
                    }
                } else if (action.payload?.value) {
                    const w = changeBtWidth(action.payload.value.length);

                    state.currentSearch.hasValue = true;
                    state.currentSearch.billTos = action.payload.value;
                    state.currentSearch.billToWidth = w;
                }
            } else if (action.payload?.value) {
                const w = changeBtWidth(action.payload.value.length);
                state.currentSearch.hasValue = true;
                state.currentSearch.billTos = action.payload.value;
                state.currentSearch.billToWidth = w;
            }
        },
        setSearchMode: (
            state,
            action: PayloadAction<MultiSelectDispatchOptions<ModeVm>>
        ) => {
            state.currentSearch.hasValue = true;
            state.currentSearch.mode = action.payload.value;
        },
        setSearchEntryMethod: (
            state,
            action: PayloadAction<EntryMethodDispatchOptions>
        ) => {
            //Workaround to handle Entry Methods structure being added later
            if (state.currentSearch.entryMethods === undefined) {
                state.currentSearch.entryMethods = [];
            }

            //Handle defaulting in things to a proper structure
            let newState = [...state.currentSearch.entryMethods];
            if (newState.length === 0) {
                newState = [
                    {code: 'edi', selected: true},
                    {code: 'paper', selected: true},
                ];
            }

            newState.find(
                (meth) => meth.code === action.payload.method
            ).selected = action.payload.checked;

            state.currentSearch.entryMethods = newState;
        },
        setInvoiceTypeMethod: (
            state,
            action: PayloadAction<EntryMethodDispatchOptions>
        ) => {
            //Workaround to handle Invoice Types being added later
            if (state.currentSearch.invoiceTypes === undefined) {
                state.currentSearch.invoiceTypes = [];
            }

            //Handle defaulting in things
            let newState = [...state.currentSearch.invoiceTypes];
            if (newState.length === 0) {
                newState = [
                    {code: 'carrinv', selected: true},
                    {code: 'memo', selected: true},
                    {code: 'deleted', selected: false},
                    {code: 'custof', selected: false},
                    {code: 'ediTest', selected: false},
                ];
            }

            newState.find(
                (invType) => invType.code === action.payload.method
            ).selected = action.payload.checked;

            state.currentSearch.invoiceTypes = newState;
        },

        setSearchDateRange: (
            state,
            action: PayloadAction<DateRangeDispatchOptions>
        ) => {
            state.currentSearch.hasValue = true;

            //Workaround to handle the "Entered" date range being added later
            if (state.currentSearch[action.payload.dateType] === undefined) {
                state.currentSearch[action.payload.dateType] = {};
            }

            state.currentSearch[action.payload.dateType][
                action.payload.property
            ] = action.payload?.value?.format('YYYY-MM-DD');
        },
        setSearchInvoices: (state, action: PayloadAction<string>) => {
            state.currentSearch.hasValue = true;
            state.currentSearch.invoices = action.payload;
        },
        setSearchAirBillNum: (state, action: PayloadAction<string>) => {
            state.currentSearch.hasValue = true;
            state.currentSearch.airBillNums = action.payload;
        },
        setSearchChecks: (state, action: PayloadAction<string>) => {
            state.currentSearch.hasValue = true;
            state.currentSearch.checks = action.payload;
        },
        setSearchStatusCodes: (
            state,
            action: PayloadAction<MultiSelectDispatchOptions<StatusCodeVm>>
        ) => {
            state.currentSearch.hasValue = true;
            state.currentSearch.statusCodes = action.payload.value;
        },
        setSearchAddress: (
            state,
            action: PayloadAction<AddressDispatchOptions>
        ) => {
            state.currentSearch.hasValue = true;
            if (!state.currentSearch[action.payload.addressType]) {
                state.currentSearch[action.payload.addressType] = {};
            }
            state.currentSearch[action.payload.addressType][
                action.payload.property
            ] = action.payload.value;
        },
        setSearchFindReference: (state, action: PayloadAction<string>) => {
            state.currentSearch.hasValue = true;
            state.currentSearch.findReference = action.payload;
        },
        setSearchReferenceCodes: (
            state,
            action: PayloadAction<
                MultiSelectDispatchOptions<AscReferenceCodeVm>
            >
        ) => {
            state.currentSearch.hasValue = true;
            state.currentSearch.referenceCodes = action.payload.value;
        },
        setSessionColumnState: (state, action: PayloadAction<ColumnState>) => {
            state.sessionColumnState = action.payload;
        },
        setSearchReferenceType: (state, action: PayloadAction<string>) => {
            state.currentSearch.hasValue = true;
            state.currentSearch.referenceSearchType = action.payload;
        },
        setEditInvoiceMode: (state, action: PayloadAction<boolean>) => {
            state.editInvoices = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(logout.pending, () => {
            return baseSessionState;
        });
        builder.addMatcher(
            paylogikApi.endpoints.getBillToForDisplay.matchFulfilled,
            (state, {payload}) => {
                //@ts-ignore
                state.availableBillTos = payload;
            }
        );
        builder.addMatcher(
            paylogikApi.endpoints.getInvoiceResearchPopulationData
                .matchFulfilled,
            (state, {payload}) => {
                //@ts-ignore
                state.availableBillTos = payload.billTos;
            }
        );
    },
});

export const {
    addInvoiceTab,
    removeInvoiceTab,
    setCurrentTab,
    setCurrentPage,
    setItemsPerPage,
    setDeclaredCount,
    setGridSort,
    resetPaging,
    resetInvoiceSearch,
    setInvoiceSearchValues,
    setSearchCarrier,
    setSearchBillTo,
    setSearchMode,
    setSearchEntryMethod,
    setInvoiceTypeMethod,
    setSearchDateRange,
    setSearchInvoices,
    setSearchAirBillNum,
    setSearchChecks,
    setSearchStatusCodes,
    setSearchAddress,
    setSearchFindReference,
    setSearchReferenceCodes,
    setSearchReferenceType,
    setSessionColumnState,
    setEditInvoiceMode,
} = slice.actions;

export default slice.reducer;

//export const selectCurrentUser = (state: RootState) => state.auth.user;
